import { History, Info } from "lucide-react";
import { useMemo } from "react";

import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { ColocaDrawer } from "@/shared/components/overlays/ColocaDrawer";
import useTranslations from "@/shared/lang/useTranslations";

import { useFeeLabels } from "../../hooks/useFeeLabels";
import type { ProviderRailRoute } from "../../types/paymentFees.types";
import type { PaymentTax } from "../../types/paymentTaxes.types";
import { corridorLabel } from "../../utils/feeFormat";
import { corridorTaxChanges } from "../../utils/taxCoverage";
import { TaxChangeTimeline } from "./TaxChangeTimeline";

export interface TaxCorridorHistoryDrawerProps {
  route: ProviderRailRoute;
  organizationId: string | null;
  organizationName: string | null;
  onClose: () => void;
  rows: PaymentTax[];
  now: number;
  organizationNames: Record<string, string>;
  authorNames: Record<string, string>;
}

export function TaxCorridorHistoryDrawer({
  route,
  organizationId,
  organizationName,
  onClose,
  rows,
  now,
  organizationNames,
  authorNames,
}: TaxCorridorHistoryDrawerProps) {
  const { t } = useTranslations("paymentTaxes");
  const labels = useFeeLabels();
  const changes = useMemo(
    () => corridorTaxChanges(route, organizationId, rows, now),
    [route, organizationId, rows, now],
  );

  return (
    <ColocaDrawer
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={t("history_drawer_title", { corridor: corridorLabel(route, labels.directionShort) })}
      description={
        organizationId
          ? t("history_drawer_desc_org", { name: organizationName ?? organizationId })
          : t("history_drawer_desc_general")
      }
      size="lg"
      pinLastChild={false}
    >
      <div className="space-y-4">
        {changes.length === 0 ? (
          <ColocaEmpty icon={History} title={t("history_empty")} surface="card" />
        ) : (
          <TaxChangeTimeline
            changes={changes}
            organizationNames={organizationNames}
            authorNames={authorNames}
          />
        )}
        <ColocaAlert variant="secondary" icon={Info} text={t("history_deleted_note")} />
      </div>
    </ColocaDrawer>
  );
}
